// Pure, dependency-free helpers for the sign-in verify-device modal.
// Kept out of Login.js so they can be unit-tested (Login.js is not Jest-renderable here).

export const VERIFY_CHANNELS = { SMS: "sms", EMAIL: "email" };

const KNOWN_CHANNELS = [VERIFY_CHANNELS.SMS, VERIFY_CHANNELS.EMAIL];

/**
 * Feature-detect channel info from the /web/login response. The platform may
 * (future) include channel / maskedDestination / availableChannels. Returns
 * nulls when absent so the modal falls back to neutral copy.
 */
export function resolveVerifyChannel(loginResponse) {
  const resp = loginResponse || {};
  const channel = KNOWN_CHANNELS.includes(resp.channel) ? resp.channel : null;
  const rawMasked = typeof resp.maskedDestination === "string" ? resp.maskedDestination.trim() : "";
  // Defense in depth: if the backend accidentally sends an unmasked number
  // (7+ consecutive digits), discard it rather than render it in the UI.
  const maskedDestination = rawMasked && !/\d{7,}/.test(rawMasked) ? rawMasked : null;
  const availableChannels = Array.isArray(resp.availableChannels)
    ? resp.availableChannels.filter((c) => KNOWN_CHANNELS.includes(c))
    : null;
  return { channel, maskedDestination, availableChannels };
}

/** jbaldonado@lucashealth.ai -> j•••@lucashealth.ai ; "" if not a valid email. */
export function maskEmail(email) {
  if (typeof email !== "string" || !email.includes("@")) return "";
  const atIdx = email.lastIndexOf("@");
  const local = email.slice(0, atIdx);
  const domain = email.slice(atIdx + 1);
  if (!local || !domain) return "";
  return `${local[0]}•••@${domain}`;
}

/**
 * Mask a phone number to the last two digits: "+1 555 123 4589" → "•••89".
 * Returns "" for non-strings or strings with fewer than 2 digit characters.
 */
export function maskPhone(phone) {
  if (typeof phone !== "string") return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 2) return "";
  return "•••" + digits.slice(-2);
}

/** Pull a 6-digit code out of arbitrary text; strips non-digits first. */
export function extractOtpCode(text) {
  if (typeof text !== "string") return null;
  const match = text.replace(/\D/g, "").match(/\d{6}/);
  return match ? match[0] : null;
}

/** Base login error classification (moved verbatim from Login.js). */
export function classifyLoginError(loginResponse) {
  const axiosErr = loginResponse?.cause;
  if (!axiosErr?.response) return "network";
  const status = axiosErr.response.status;
  if (status === 429) return "rateLimited";
  if (status >= 500) return "server";
  if (sessionLimitOf(loginResponse)) return "sessionLimit";
  return "credentials";
}

/**
 * The platform's refusal of a sign-in because the account already holds the organization's cap of concurrent
 * sessions (a 403 whose body is `{error: "concurrent_sessions", maxSessions, idleTimeoutMinutes}`), as
 * `{max, minutes}` for the message, or null for any other response. The password was right: this must never read
 * as a credential error.
 */
export function sessionLimitOf(loginResponse) {
  const response = loginResponse?.cause?.response;
  if (response?.status !== 403) return null;
  let data = response.data;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (_e) {
      return null;
    }
  }
  if (data?.error !== "concurrent_sessions") return null;
  return { max: Number(data.maxSessions) || 0, minutes: Number(data.idleTimeoutMinutes) || 0 };
}

/**
 * Whether a failed verify/resend was due to no mobile number on file.
 *
 * STAGE CONFIRMATION REQUIRED — the exact response shape is NOT yet confirmed
 * from the real backend. This checks the most common shapes conservatively.
 * Once a real staged "no-phone" response is observed, tighten this predicate.
 */
export function isNoMobileSignal(loginResponse) {
  const noPhonePattern = /no.?(mobile|phone).?(number|on.?file)|phone.?not.?(found|set|registered)|missing.?phone/i;
  // Path 1: error response (4xx/5xx) — the most likely shape for a failed send.
  const data = loginResponse?.cause?.response?.data;
  if (data) {
    const code = String(data?.code ?? data?.errorCode ?? "");
    if (noPhonePattern.test(code)) return true;
    const msg = String(data?.message || data?.error || "");
    if (noPhonePattern.test(msg)) return true;
  }
  // Path 2: a "success" body (200) that still carries a no-phone indicator.
  const directMsg = String(loginResponse?.message || loginResponse?.error || "");
  if (noPhonePattern.test(directMsg)) return true;
  return false; // Conservative default: don't hide SMS when uncertain.
}

/**
 * Whether a failed verify was due to an EXPIRED code (vs simply wrong).
 * The exact platform signal must be confirmed during stage smoke; this checks
 * the common shapes defensively. If the platform gives no signal this returns
 * false and the caller shows the wrong-code copy (which already says
 * "or request a new one"), so we never assert "wrong" when it might be expired.
 */
export function isExpiredSignal(loginResponse) {
  const data = loginResponse?.cause?.response?.data;
  const code = data?.code || data?.errorCode;
  if (typeof code === "string" && /expir/i.test(code)) return true;
  const msg = data?.message || data?.error;
  if (typeof msg === "string" && /expir/i.test(msg)) return true;
  return false;
}

/** Verify-modal classification: base, upgraded to "expired" when signalled. */
export function classifyVerifyError(loginResponse) {
  const base = classifyLoginError(loginResponse);
  if (base === "credentials" && isExpiredSignal(loginResponse)) return "expired";
  return base;
}
